import React, { useState, useEffect, useRef } from "react";
import {
  MultiSubListComponent,
  ValidationRule,
} from "../../services/types/ComponentTypes";

interface MultiSubListProps {
  config: MultiSubListComponent;
  value?: Record<string, any>[];
  onChange?: (name: string, value: Record<string, any>[]) => void;
  lookupData?: any[]; // Lookup data passed from parent
}

interface InspectionItem {
  id: string | number;
  value?: string | number; // For lookup items
  name?: string;
  label?: string;
  sl_no?: number;
  itemid?: string | number;
  [key: string]: any; // Allow other properties from lookup
}

interface ChecklistItem {
  sl_no: number | string;
  inspection_item: string;
  checked_a: boolean;
  checked_b: boolean;
  checked_c: boolean;
  itemid?: string | number; // Include itemid from lookup
  id?: string; // For form submission
  [key: string]: any; // Allow other properties
}

export const MultiSubList: React.FC<MultiSubListProps> = ({
  config,
  value = [],
  onChange,
  lookupData = [],
}) => {
  const [error, setError] = useState<string>("");
  const [data, setData] = useState<ChecklistItem[]>(value as ChecklistItem[]);
  const [inspectionItems, setInspectionItems] = useState<InspectionItem[]>([]);
  const isInitializedRef = useRef(false);

  // Initialize inspection items from lookup or use mock data
  useEffect(() => {
    if (lookupData && lookupData.length > 0) {
      // Use lookup data if provided
      const items: InspectionItem[] = lookupData.map(
        (item: any, index: number) => {
          // Handle different lookup data formats
          const id = item.id || item.value || item.itemid || index + 1;
          const name =
            item.name ||
            item.label ||
            item.inspection_item ||
            String(item.value || "");
          const slNo = item.sl_no || item.slno || index + 1;
          const itemId = item.itemid || item.id || item.value || id;

          return {
            id,
            name,
            sl_no: slNo,
            itemid: itemId,
            ...item, // Include all other properties
          };
        }
      );
      setInspectionItems(items);
    } else if (!(config as any).LookupName) {
      // Fallback to mock data ONLY if no lookup is configured
      const mockItems: InspectionItem[] = [
        { id: 1, name: "Engine Oil Level", sl_no: 1, itemid: "1" },
        { id: 2, name: "Brake Fluid Level", sl_no: 2, itemid: "2" },
        { id: 3, name: "Coolant Level", sl_no: 3, itemid: "3" },
        { id: 4, name: "Power Steering Fluid", sl_no: 4, itemid: "4" },
        { id: 5, name: "Windshield Washer Fluid", sl_no: 5, itemid: "5" },
        { id: 6, name: "Battery Condition", sl_no: 6, itemid: "6" },
        { id: 7, name: "Tire Pressure", sl_no: 7, itemid: "7" },
        { id: 8, name: "Tire Tread Depth", sl_no: 8, itemid: "8" },
        { id: 9, name: "Headlights", sl_no: 9, itemid: "9" },
        { id: 10, name: "Taillights", sl_no: 10, itemid: "10" },
        { id: 11, name: "Turn Signals", sl_no: 11, itemid: "11" },
        { id: 12, name: "Horn", sl_no: 12, itemid: "12" },
        { id: 13, name: "Wipers", sl_no: 13, itemid: "13" },
        { id: 14, name: "Mirrors", sl_no: 14, itemid: "14" },
        { id: 15, name: "Seat Belts", sl_no: 15, itemid: "15" },
      ];
      setInspectionItems(mockItems);
    }
  }, [lookupData]);

  // Initialize data from lookup items and merge with existing value if present
  useEffect(() => {
    if (isInitializedRef.current || inspectionItems.length === 0) return;

    // Always initialize from ALL inspection items (lookup data)
    // This ensures all available items are shown, not just ones from value prop
    const initialData = inspectionItems.map((item) => {
      const itemId = item.itemid || item.id || String(item.value || "");

      // Try to find existing data for this item from value prop
      const existingItem =
        value && Array.isArray(value)
          ? value.find((v: any) => {
            const existingItemId = v.itemid || v.id || String(v.value || "");
            // Match by ID first
            if (String(existingItemId) === String(itemId)) return true;
            // Fallback: Match by Name/Label
            const vName = v.inspection_item || v.name || v.label;
            const iName = item.name || item.label || item.inspection_item;
            return (
              vName && iName && String(vName).trim() === String(iName).trim()
            );
          })
          : null;

      // If existing item found, preserve its checked states, otherwise use defaults
      return {
        sl_no: item.sl_no || 0,
        inspection_item: item.name || item.label || "",
        checked_a: existingItem ? Boolean(existingItem.checked_a) : false,
        checked_b: existingItem ? Boolean(existingItem.checked_b) : false,
        checked_c: existingItem ? Boolean(existingItem.checked_c) : false,
        itemid: itemId,
        // Preserve other properties from existing item if present
        ...(existingItem ? { id: existingItem.id } : {}),
      };
    });

    isInitializedRef.current = true;
    setData(initialData);

    // Call onChange after state update completes
    Promise.resolve().then(() => {
      const configAny = config as any; // Handle legacy BindValue property
      onChange?.(config.name || configAny.BindValue || "", initialData);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.name, inspectionItems]); // Initialize when inspectionItems are ready

  // Sync with value prop when it changes externally (merge with existing data)
  useEffect(() => {
    // We need inspectionItems to be ready before we can sync
    if (inspectionItems.length === 0) return;

    // If value is provided (even if empty, but we care mostly when it has data for edit mode)
    if (value && Array.isArray(value)) {
      if (import.meta.env.DEV) {
        console.log(`[MultiSubList] Syncing value prop (length: ${value.length}) with inspection items`, {
          valuePropSample: value[0],
        });
      }

      // Re-generate data by merging inspection items with value prop
      // This ensures we always show all inspection items, but with states from value prop
      const mergedData: ChecklistItem[] = inspectionItems.map((item, index) => {
        const itemId = item.itemid || item.id || String(item.value || "");

        // Find match in value prop
        const existingItem = value.find((v: any) => {
          const existingItemId = v.itemid || v.id || String(v.value || "");
          // Debug matching
          if (import.meta.env.DEV && index === 0 && v === value[0]) {
            console.log(`[MultiSubList] Matching first item:`, {
              lookupItemId: itemId,
              valueItemId: existingItemId,
              match: String(existingItemId) === String(itemId)
            });
          }
          // Match by ID first
          if (String(existingItemId) === String(itemId)) return true;
          // Fallback: Match by Name/Label
          const vName = v.inspection_item || v.name || v.label;
          const iName = item.name || item.label || item.inspection_item;
          return (
            vName && iName && String(vName).trim() === String(iName).trim()
          );
        });

        // Use existing item's state if found, otherwise default to false
        const { id: lookupId, ...itemRest } = item;

        return {
          sl_no: item.sl_no || 0,
          inspection_item: item.name || item.label || "",
          checked_a: existingItem ? Boolean(existingItem.checked_a) : false,
          checked_b: existingItem ? Boolean(existingItem.checked_b) : false,
          checked_c: existingItem ? Boolean(existingItem.checked_c) : false,
          itemid: itemId,
          // Preserve other properties from existing item if present, otherwise from lookup
          // Ensure id is string if present
          ...(existingItem ? { id: existingItem.id ? String(existingItem.id) : undefined } : { id: lookupId ? String(lookupId) : undefined }),
          ...itemRest // Include lookup properties minus id
        };
      });

      setData(mergedData);

      // If we haven't marked as initialized, do so now
      if (!isInitializedRef.current) {
        isInitializedRef.current = true;
      }
    }
  }, [value, inspectionItems]);

  const validateData = (dataToValidate: ChecklistItem[]): string => {
    if (!config.validation) return "";

    for (const rule of config.validation) {
      switch (rule.rule) {
        case ValidationRule.REQUIRED:
          if (dataToValidate.length === 0)
            return rule.message || "Inspection items are required";
          break;
        case ValidationRule.MIN_VALUE:
          if (dataToValidate.length < (rule.value as number))
            return rule.message || `Minimum ${rule.value} items required`;
          break;
        case ValidationRule.MAX_VALUE:
          if (dataToValidate.length > (rule.value as number))
            return rule.message || `Maximum ${rule.value} items allowed`;
          break;
      }
    }
    return "";
  };

  const handleDataChange = (newData: ChecklistItem[]) => {
    setData(newData);
    const validationError = validateData(newData);
    setError(validationError);
    onChange?.(config.name, newData);
  };

  const updateCheckbox = (
    index: number,
    field: "checked_a" | "checked_b" | "checked_c",
    checked: boolean
  ) => {
    const newData = [...data];
    newData[index] = {
      ...newData[index],
      [field]: checked,
    };
    handleDataChange(newData);
  };

  const containerStyles: React.CSSProperties = {
    backgroundColor: config.style?.backgroundColor || "transparent",
    padding: config.style?.padding || 0,
    borderRadius: config.style?.borderRadius || 8,
    margin: config.style?.margin || 0,
  };

  const labelStyles: React.CSSProperties = {
    fontSize: config.style?.fontSize || 16,
    fontWeight: "600",
    color: config.style?.textColor || "#374151",
  };

  return (
    <div
      className={`border overflow-hidden ${error ? "border-2 border-danger-500 dark:border-danger-500" : "border-gray-300 dark:border-gray-600"
        }`}
      style={containerStyles}
    >
      {config.label && (
        <label className="block mb-3 bg-gray-100 dark:bg-gray-700 p-2 text-gray-900 dark:text-gray-100" style={labelStyles}>
          {config.label}
          {config.required && <span className="text-danger-500 dark:text-danger-400 ml-1">*</span>}
        </label>
      )}

      {/* Desktop Table View */}
      <div className="w-full overflow-x-auto mb-4 md:block hidden -webkit-overflow-scrolling-touch scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100">
        <table className="w-full min-w-[600px] border-collapse text-sm">
          <thead>
            <tr>
              <th className="bg-gradient-to-r from-primary-600 to-primary-700 dark:from-primary-700 dark:to-primary-800 text-white p-3 px-2 text-center font-semibold text-xs border-b-0 border-primary-500/30 whitespace-nowrap">
                SL No.
              </th>
              <th className="bg-gradient-to-r from-primary-600 to-primary-700 dark:from-primary-700 dark:to-primary-800 text-white p-3 px-2 text-center font-semibold text-xs border-b-0 border-primary-500/30 whitespace-nowrap">
                Inspection Items
              </th>
              <th className="bg-gradient-to-r from-primary-600 to-primary-700 dark:from-primary-700 dark:to-primary-800 text-white p-3 px-2 text-center font-semibold text-xs border-b-0 border-primary-500/30 whitespace-nowrap">
                Attention
              </th>
              <th className="bg-gradient-to-r from-primary-600 to-primary-700 dark:from-primary-700 dark:to-primary-800 text-white p-3 px-2 text-center font-semibold text-xs border-b-0 border-primary-500/30 whitespace-nowrap">
                Caution
              </th>
              <th className="bg-gradient-to-r from-primary-600 to-primary-700 dark:from-primary-700 dark:to-primary-800 text-white p-3 px-2 text-center font-semibold text-xs border-b-0 border-primary-500/30 whitespace-nowrap">
                Satisfaction
              </th>
            </tr>
          </thead>
          <tbody>
            {data.map((item, index) => (
              <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                <td className="p-2 px-1.5 border-b border-gray-300 dark:border-gray-600 text-center align-middle text-xs bg-gray-50 dark:bg-gray-800 font-semibold w-[50px] min-w-[50px] text-gray-900 dark:text-gray-100">
                  {item.sl_no}
                </td>
                <td className="p-2 px-1.5 border-b border-gray-300 dark:border-gray-600 text-left align-middle text-xs font-medium min-w-[150px] max-w-[200px] break-words overflow-hidden text-ellipsis text-gray-900 dark:text-gray-100">
                  {item.inspection_item}
                </td>
                <td className="p-2 px-1.5 border-b border-gray-300 dark:border-gray-600 text-center align-middle">
                  <input
                    type="checkbox"
                    className="w-5 h-5 cursor-pointer accent-success-600 dark:accent-success-500 scale-110 focus:outline-2 focus:outline-success-600 dark:focus:outline-success-500 focus:outline-offset-2"
                    checked={!!item.checked_a}
                    onChange={(e) =>
                      updateCheckbox(index, "checked_a", e.target.checked)
                    }
                    disabled={config.disabled}
                  />
                </td>
                <td className="p-2 px-1.5 border-b border-gray-300 dark:border-gray-600 text-center align-middle">
                  <input
                    type="checkbox"
                    className="w-5 h-5 cursor-pointer accent-success-600 dark:accent-success-500 scale-110 focus:outline-2 focus:outline-success-600 dark:focus:outline-success-500 focus:outline-offset-2"
                    checked={!!item.checked_b}
                    onChange={(e) =>
                      updateCheckbox(index, "checked_b", e.target.checked)
                    }
                    disabled={config.disabled}
                  />
                </td>
                <td className="p-2 px-1.5 border-b border-gray-300 dark:border-gray-600 text-center align-middle">
                  <input
                    type="checkbox"
                    className="w-5 h-5 cursor-pointer accent-success-600 dark:accent-success-500 scale-110 focus:outline-2 focus:outline-success-600 dark:focus:outline-success-500 focus:outline-offset-2"
                    checked={!!item.checked_c}
                    onChange={(e) =>
                      updateCheckbox(index, "checked_c", e.target.checked)
                    }
                    disabled={config.disabled}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="block md:hidden">
        {data.map((item, index) => (
          <div
            key={index}
            className="border border-gray-300 dark:border-gray-600 rounded-lg mb-3 p-4 bg-white dark:bg-gray-800 shadow-sm hover:shadow-md hover:-translate-y-px transition-all duration-200"
          >
            <div className="flex justify-between items-center mb-3 pb-2 border-b border-gray-300 dark:border-gray-600">
              <div className="font-semibold text-sm text-gray-700 dark:text-gray-200">
                {item.inspection_item}
              </div>
              <div className="bg-success-600 dark:bg-success-500 text-white px-2 py-1 rounded text-xs font-semibold">
                {item.sl_no}
              </div>
            </div>
            <div className="flex justify-around items-center mt-3 py-2">
              <div className="flex flex-col items-center gap-1">
                <input
                  type="checkbox"
                  className="w-6 h-6 cursor-pointer accent-success-600 scale-125 focus:outline-2 focus:outline-success-600 focus:outline-offset-2"
                  checked={item.checked_a}
                  onChange={(e) =>
                    updateCheckbox(index, "checked_a", e.target.checked)
                  }
                  disabled={config.disabled}
                />
                <div className="text-[11px] font-medium text-gray-500 dark:text-gray-400 text-center">
                  Attention
                </div>
              </div>
              <div className="flex flex-col items-center gap-1">
                <input
                  type="checkbox"
                  className="w-6 h-6 cursor-pointer accent-success-600 dark:accent-success-500 scale-125 focus:outline-2 focus:outline-success-600 dark:focus:outline-success-500 focus:outline-offset-2"
                  checked={item.checked_b}
                  onChange={(e) =>
                    updateCheckbox(index, "checked_b", e.target.checked)
                  }
                  disabled={config.disabled}
                />
                <div className="text-[11px] font-medium text-gray-500 dark:text-gray-400 text-center">
                  Caution
                </div>
              </div>
              <div className="flex flex-col items-center gap-1">
                <input
                  type="checkbox"
                  className="w-6 h-6 cursor-pointer accent-success-600 dark:accent-success-500 scale-125 focus:outline-2 focus:outline-success-600 dark:focus:outline-success-500 focus:outline-offset-2"
                  checked={item.checked_c}
                  onChange={(e) =>
                    updateCheckbox(index, "checked_c", e.target.checked)
                  }
                  disabled={config.disabled}
                />
                <div className="text-[11px] font-medium text-gray-500 dark:text-gray-400 text-center">
                  Satisfaction
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {error && (
        <div className="text-danger-500 text-sm mt-2 flex items-center">
          <span className="mr-1">⚠️</span>
          {error}
        </div>
      )}

      {config.helperText && !error && (
        <div className="text-gray-500 text-sm mt-2">{config.helperText}</div>
      )}
    </div>
  );
};
